import { useCallback, useMemo, useState } from 'react';
import { WeightedGroupRef } from '../../../types/AssignmentsAssignTo';

interface GroupOption {
  value: string;
  label: string;
}

interface UseWeightedGroupsSelectorProps {
  weightedGroups: WeightedGroupRef[];
  onChange: (weightedGroups: WeightedGroupRef[]) => void;
  groupOptions: GroupOption[];
  onOpenDropdown?: () => void;
}

export const useWeightedGroupsSelector = ({
  weightedGroups,
  onChange,
  groupOptions,
  onOpenDropdown,
}: UseWeightedGroupsSelectorProps) => {
  const [showAddPicker, setShowAddPicker] = useState(false);

  const showWeightsAndDelete = weightedGroups.length > 1;

  const totalWeight = useMemo(
    () => weightedGroups.reduce((sum, group) => sum + (group.weight || 0), 0),
    [weightedGroups],
  );

  const getAvailableOptions = useCallback(
    (currentGroupId?: string) => {
      const selectedIds = weightedGroups
        .map((g) => g.groupId)
        .filter((id) => id !== currentGroupId);
      return groupOptions.filter((opt) => !selectedIds.includes(opt.value));
    },
    [weightedGroups, groupOptions],
  );

  const handleGroupChange = useCallback(
    (index: number, groupId: string, groupName: string) => {
      const newGroups = [...weightedGroups];
      newGroups[index] = {
        ...newGroups[index],
        groupId,
        _name: groupName,
      };
      onChange(newGroups);
    },
    [weightedGroups, onChange],
  );

  const handleWeightChange = useCallback(
    (index: number, weight: number | null) => {
      const newGroups = [...weightedGroups];
      newGroups[index] = {
        ...newGroups[index],
        weight: weight ?? 1,
      };
      onChange(newGroups);
    },
    [weightedGroups, onChange],
  );

  const handleDeleteGroup = useCallback(
    (index: number) => {
      const newGroups = weightedGroups.filter((_, i) => i !== index);
      onChange(newGroups);
    },
    [weightedGroups, onChange],
  );

  const handleAddGroupClick = useCallback(() => {
    setShowAddPicker(true);
    onOpenDropdown?.();
  }, [onOpenDropdown]);

  const handleAddGroupSelect = useCallback(
    (groupId: string, groupName: string) => {
      onChange([
        ...weightedGroups,
        {
          groupId,
          weight: 1,
          _name: groupName,
        },
      ]);
      setShowAddPicker(false);
    },
    [weightedGroups, onChange],
  );

  const handleAddPickerClose = useCallback(() => {
    setShowAddPicker(false);
  }, []);

  const getDisplayValue = useCallback(
    (group: WeightedGroupRef) => {
      if (group._name) return group._name;
      const option = groupOptions.find((opt) => opt.value === group.groupId);
      return option?.label || '';
    },
    [groupOptions],
  );

  const availableOptionsForAdd = getAvailableOptions();

  return {
    showAddPicker,
    showWeightsAndDelete,
    totalWeight,
    availableOptionsForAdd,
    getAvailableOptions,
    getDisplayValue,
    handleGroupChange,
    handleWeightChange,
    handleDeleteGroup,
    handleAddGroupClick,
    handleAddGroupSelect,
    handleAddPickerClose,
  };
};
